import { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { Session } from '@beyond-js/ui/dom';
import type { Descriptor, Names, Place } from '@snapshots/domain';
import type { Arrival } from './arrival';

/** What the shell shares with the screen inside it: where the person is, and what the family bar read */
export interface Frame {
	/**
	 * Names the project and organization in view, for the family bar: `{}` when the screen shows none,
	 * `undefined` while it does not know yet (the bar keeps what it shows until then)
	 */
	place: (place: Place | undefined) => void;
	/** The family bar's descriptor: `null` while it loads, `{unavailable: true}` when it cannot be read */
	descriptor: Descriptor | { unavailable: true } | null;
	/** The names of organizations and projects this application has been told, by identifier */
	names: Names;
	/** Focus for the main heading of a screen reached by moving inside the application */
	arrival: Arrival;
	/** The family's answer to a session that ended (`Session`), once someone is signed in */
	renewal: Session | null;
	/** How many times the session was renewed: what a screen shows is read again after each */
	renewed: number;
	/** Resolves at the next renewal, for a list whose request the ended session kept from being sent */
	resumed: () => Promise<void>;
}

export function useFrame(): Frame {
	return useOutletContext<Frame>();
}

/**
 * Reports where a screen is while it is shown: `undefined` until it knows, then the project and
 * organization it shows, or `{}` for none
 */
export function usePlace(where: Place | undefined): void {
	const { place } = useFrame();
	const project = where?.project;
	const organization = where?.organization;
	const known = where !== undefined;
	useEffect(() => {
		if (!known) return;
		place({ project, organization });
		return () => place(undefined);
	}, [place, known, project, organization]);
}
